import * as KendoChart from '@progress/kendo-react-charts'
import { DateRangePicker } from '@progress/kendo-react-dateinputs'
import bodybuilder from 'bodybuilder'
import { DropdownComponent } from 'components/dropdown/component'
import { HeaderComponent } from 'components/header/component'
import { format } from 'date-fns'
import { AggregateIntervalOptions, DateRangesAsDropdownOptions } from 'elasticsearch/constants'
import { AggregationType, DateRanges, FilterType, IndexName, toFilter } from 'elasticsearch/constants'
import { ResponseAggregate, ResponsePayload } from 'elasticsearch/types'
import {
  ElasticsearchComponent,
  ElasticsearchProps,
} from 'generic/elasticSearchComponent'
import * as GenericRedux from 'generic/genericRedux'
import * as React from 'react'
import * as Constants from './constants'
import { RasaAnalyticsComponent } from './rasa-analytics-component'
import './styles.css'
import { ConnectedComponentClass } from "react-redux"
import {Fields} from "../../shared/modals";
import {ComponentType} from "react";
import { Dataset } from '../../generic/dataset'
import { PROGRESS_STATUS } from './constants'

const DEFAULT_THRESHOLD: DefaultThreshold = {
  opens: {
    poor: 10,
    good: 20,
    excellent: 30
  },
  click_through: {
    poor: 10,
    good: 20,
    excellent: 30
  },
  hard_bounce: {
    poor: 10,
    good: 20,
    excellent: 30
  },
  soft_bounce: {
    poor: 10,
    good: 20,
    excellent: 30
  }
}
interface ProgressThreshold {
  poor: number,
  good: number,
  excellent: number
}

interface DefaultThreshold {
  opens: ProgressThreshold,
  click_through: ProgressThreshold,
  hard_bounce: ProgressThreshold,
  soft_bounce: ProgressThreshold
}

const initialState = {
  selectedDateRange: DateRangesAsDropdownOptions[2],
  selectedInterval: AggregateIntervalOptions[0],
  email_health_report_threshold: DEFAULT_THRESHOLD
}
export class EmailHealthComponent extends RasaAnalyticsComponent<any, any> {
  constructor(props) {
    super(props, initialState)
  }

  public componentDidMount() {
    super.componentDidMount()
    this.minCustomDateRange()
    this.context.user.init().then(({activeCommunity}) => {
      const params = [
        {param: 'key', value: 'email_health_report_threshold'}
      ]
      new Dataset().loadCommunityDataset('communityConfiguration', activeCommunity.communityId, params)
        .then((configuration) => {
          if (configuration.length > 0 && configuration[0].length > 0) {
            this.setState({
              email_health_report_threshold: {...JSON.parse(configuration[0][0].value)},
            })
          }
        })
    })
  }

  public render() {
    return(
      <div className="analytics-component">
        <HeaderComponent
          title={'ANALYTICS'}
          subTitle={'Email Health History'}
          description={[
            'Opens - The number of times your newsletter has been opened by your subscribers.',
            'Clicks - The number of times any of your subscribers have clicked on a link in your newsletter.',
            'Click to Open - the number of clicks out of the number of opens',
          ]}
        />
        <div className="dropdown-header">
          <div className="dropdown-inline">
            <DropdownComponent data={DateRangesAsDropdownOptions}
                               selected={this.state.selectedDateRange.key}
                               onChange={this.dateChanged}/>
          </div>
          <div className="dropdown-inline">
            <DropdownComponent data={AggregateIntervalOptions}
                               selected={this.state.selectedInterval.key}
                               onChange={this.intervalChanged}/>
          </div>
        </div>
        <div className="date-range-picker">
          {this.state.selectedDateRange.key === '7' ?
            <DateRangePicker min={this.state.minCustomDateRange}
                             max ={new Date()}
                             onChange={this.createCustomDate} /> : null}
        </div>
        <AnalyticsOpensClicksComponent dateRange={this.state.selectedDateRange.value}
                                       selectedInterval={this.state.selectedInterval.value}
                                       threshold={this.state.email_health_report_threshold}/>
      </div>
    )
  }
}

const totalOpenColor = '#004f6d'
const uniqueBouncesColor = '#4ecdc4'
const uniqueSoftBouncesColor = '#ff6b6b'
const totalClickColor = '#8ae0e3'

interface Point {
  timestamp: string,
  totalClick: number,
  totalClickRate: number,
  totalDelivered: number,
  totalOpen: number,
  totalOpenRate: number,
  uniqueClick: number,
  uniqueClickRate: number,
  uniqueOpen: number,
  uniqueOpenRate: number,
  uniqueClickToOpenRate?: number,
  uniqueBounces?: number,
  uniqueSoftBounces?:number
}

type Points = Point

interface EngagementProps extends ElasticsearchProps<Points> {
  dateRange: string,
  selectedInterval: string,
  hideGrid: boolean,
  threshold?: any
}

interface EngagementState {
  loaded: boolean,
  displayLabel: boolean,
  record: any
}

const EVENT_AGGREGATION: string = 'event'
const DATE_AGGREGATION: string = 'date'

class OpensClicksClass extends ElasticsearchComponent<Points, EngagementProps, EngagementState> {

  constructor(p: EngagementProps) {
    super(p, IndexName.EVENTS)
    this.state = {
      loaded: false,
      displayLabel: true,
      record: []
    }
    this.reportName = Constants.REPORT_NAMES.EMAIL_HEALTH
  }

  public parseResponse(payload: ResponsePayload): Promise<Points> {
    this.setState({
      loaded: true,
    })
    const dateAggregationsBuckets = payload.aggregations[DATE_AGGREGATION].buckets
      .filter((aggregation: ResponseAggregate) => {
        return this.getAggregation(aggregation.child, 'delivered')
      })
    return Promise.resolve(dateAggregationsBuckets.map((aggregation: ResponseAggregate) => {
      const clicks = this.getAggregation(aggregation.child, 'click')
      const opens = this.getAggregation(aggregation.child, 'open')
      const delivered = this.getAggregation(aggregation.child, 'delivered')
      const bounces = this.getAggregation(aggregation.child, 'hard_bounce')
      const unique_soft_bounces = this.getAggregation(aggregation.child, 'soft_bounce')
      const dropped = this.getAggregation(aggregation.child, 'dropped')
      const d = new Date(new Date(aggregation.key))
      const dateWithoutOffset = d.setMinutes(d.getMinutes() + d.getTimezoneOffset())
      const partial: any = {
        timestamp: format(new Date(dateWithoutOffset), 'iii, MMM do'),
        totalClick: clicks ? clicks.doc_count : 0,
        totalDelivered: delivered ? delivered.doc_count : 0,
        totalOpen: opens ? opens.doc_count : 0,
        uniqueClick: clicks && clicks.unique ? clicks.unique.value : 0,
        uniqueOpen: opens && opens.unique ? opens.unique.value : 0,
        uniqueBounces: (bounces && bounces.unique ? bounces.unique.value : 0) +
          (dropped && dropped.unique ? dropped.unique.value : 0),
        uniqueSoftBounces: unique_soft_bounces && unique_soft_bounces.unique ? unique_soft_bounces.unique.value : 0,
      }
     return {
        ...partial,
        totalClickRate: (
          (partial.totalDelivered > 0 ?
            partial.totalClick / partial.totalDelivered : 0) * 100.0).toFixed(2),
        totalOpenRate: (
          (partial.totalDelivered > 0 ?
            partial.totalOpen / partial.totalDelivered : 0) * 100.0).toFixed(2),
        uniqueClickRate: (
          (partial.totalDelivered > 0 ?
            partial.uniqueClick / partial.totalDelivered : 0) * 100.0).toFixed(2),
        uniqueOpenRate: (
          (partial.totalDelivered > 0 ?
            partial.uniqueOpen / partial.totalDelivered : 0) * 100.0).toFixed(2),
        uniqueClickToOpenRate: (
          (partial.uniqueClick > 0 && partial.uniqueOpen > 0 ?
            partial.uniqueClick / partial.uniqueOpen : 0) * 100.0).toFixed(2),
        uniqueBounces:  (
          (partial.uniqueBounces > 0 && partial.uniqueBounces > 0 ?
            partial.uniqueBounces / partial.uniqueBounces : 0) * 100.0).toFixed(2),
        uniqueSoftBounces:  (
          (partial.uniqueSoftBounces > 0 && partial.uniqueSoftBounces > 0 ?
            partial.uniqueSoftBounces / partial.uniqueSoftBounces : 0) * 100.0).toFixed(2),
      }
    })).then((dataArray) => {
      this.setState({
        record: dataArray
      })
      const aggregatedData: Points = {
        timestamp: '',
        totalClick: 0,
        totalClickRate: 0,
        totalDelivered: 0,
        totalOpen: 0,
        totalOpenRate: 0,
        uniqueClick: 0,
        uniqueClickRate: 0,
        uniqueOpen: 0,
        uniqueOpenRate: 0,
        uniqueBounces:0,
        uniqueSoftBounces:0
      }

      for (const obj of dataArray) {
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key) && key !== 'timestamp') {
            aggregatedData[key] = (aggregatedData[key] || 0) + parseFloat(obj[key])
          }
        }
      }
      for (const key in aggregatedData) {
        if (Object.prototype.hasOwnProperty.call(aggregatedData, key)) {
          const calculatedValue = parseFloat(aggregatedData[key]) / 100 || 0
          aggregatedData[key] = Math.min(calculatedValue, 100).toFixed(2)
        }
      }
      if (dataArray && dataArray.length) {
        const aggregatedPoint: Point = {
          timestamp: dataArray[0].timestamp,
          ...aggregatedData,
        }
        return aggregatedPoint
      }
    })
  }

  public componentDidUpdate(oldProps: EngagementProps) {
    if ( this.props.dateRange !== oldProps.dateRange ) {
      this.search()
      this.tooMuchData()
    }
    if ( this.props.selectedInterval !== oldProps.selectedInterval) {
      this.search()
      this.tooMuchData()
    }
  }

  public searchPayload(): any {
    const search = bodybuilder().size(0)
      .filter(FilterType.range, 'message_send_date', toFilter(this.props.dateRange || DateRanges.PastMonth))

    return this.addAggregation(search, {
      type: AggregationType.date_histogram,
      field: 'message_send_date',
      extra: { interval: this.props.selectedInterval || 'day' },
      name: DATE_AGGREGATION,
      child: {
        type: AggregationType.terms,
        field: 'event_name.keyword',
        name: EVENT_AGGREGATION
      },
    }).build()
  }

  public tooMuchData = () => {
    const display = (this.props.results)
    this.setState({displayLabel: !display})
  }

  public tagTitle = (number:number,rate: ProgressThreshold) => {
    if (rate) {
      if (rate.poor > number) {
        return PROGRESS_STATUS.POOR
      } else if(rate.good > number) {
        return PROGRESS_STATUS.GENERAL
      } else {
        return PROGRESS_STATUS.EXCELLENT
      }
    } else {
      return PROGRESS_STATUS.POOR
    }

}

public tagSubtitle = (number:number,rate:ProgressThreshold) => {
    if (rate) {
      if (rate.poor > number) {
        return rate.poor
      } else if(rate.good > number) {
        return rate.good
      } else {
        return rate.excellent
      }
    } else {
      return PROGRESS_STATUS.POOR
    }
}
  public render = () => {
    const totalOpen = this.props.results ? this.props.results.totalOpen : 0
    const uniqueBounces = this.props.results ? this.props.results.uniqueBounces : 0
    const uniqueSoftBounces = this.props.results ? this.props.results.uniqueSoftBounces : 0
    const totalClicks = this.props.results ? this.props.results.totalClick : 0
    const totalDelivered = this.props.results ? this.props.results.totalDelivered : 0
    const opens = this.props.threshold.opens
    const clickThrough = this.props.threshold.click_through
    const hardBounce = this.props.threshold.hard_bounce
    const softBounce = this.props.threshold.soft_bounce
    return (
      <>
        {this.state.record && this.state.record.length > 0 ?
        <div className='statistics-top-wrapper'>
          <div className="statistic-main-wrap">
            <div className='statistics-chart d-flex flex-column align-items-center'>
              <strong className='statistics-chart-title'>YOUR SCORE</strong>
              <div className='statistics-chart-round --warning'>
                <span className="overlay"></span>
              </div>
              <strong className='statistics-chart-right'>4</strong>
              <strong className='statistics-chart-subtitle'>Needs work</strong>
              <strong className='statistics-chart-subtext'>Apr 26 - May 26</strong>
            </div>
            <div className="statistic-summary">
              <div className="summary-description">
                <strong>Summary</strong>
                <span>Score over time</span>
              </div>
              <div className="metrics-description">
                <strong>Metrics</strong>
                <ul>
                  <li>
                    <span>Opens</span>
                    <div
                      className={`stw-tag-wrapper ${this.tagTitle(totalOpen, opens) === 'Poor' ? '' : '--al-success'}`}>
                      {this.tagTitle(totalOpen, opens)}</div>
                  </li>
                  <li>
                    <span>Click-Through</span>
                    <div
                      className={`stw-tag-wrapper ${this.tagTitle(totalClicks, clickThrough) === 'Poor' ? '' : '--al-success'}`}>
                      {this.tagTitle(totalClicks, clickThrough)}</div>
                  </li>
                  <li>
                    <span>Hard bounces</span>
                    <div
                      className={`stw-tag-wrapper ${this.tagTitle(uniqueBounces, hardBounce) === 'Poor' ? '' : '--al-success'}`}>
                      {this.tagTitle(uniqueBounces, hardBounce)}</div>
                  </li>
                  <li>
                    <span>Soft Bounce</span>
                    <div
                      className={`stw-tag-wrapper ${this.tagTitle(uniqueSoftBounces, softBounce) === 'Poor' ? '' : '--al-success'}`}>
                      {this.tagTitle(uniqueSoftBounces, softBounce)}</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="list-percent-wrap">
            <ul className='list-unstyled d-flex stw-list text-center'>
              <ProgressComponent
                title={'Opens'}
                heading={`${totalOpen}%`}
                tagTitle={this.tagTitle(totalOpen, opens)}
                tagSubtitle={`${this.tagSubtitle(totalOpen, opens)}%`}
                tagClass={this.tagTitle(totalOpen, opens) === 'Poor' ? '' : '--al-success'}/>
              <ProgressComponent
                title={'CLICK THROUGH'}
                heading={`${totalClicks}%`}
                tagTitle={this.tagTitle(totalClicks, clickThrough)}
                tagSubtitle={`${this.tagSubtitle(totalClicks, clickThrough)}%`}
                tagClass={this.tagTitle(totalClicks, clickThrough) === 'Poor' ? '' : '--al-success'}/>
              <ProgressComponent
                title={'HARD BOUNCES'}
                heading={`${uniqueBounces}%`}
                tagTitle={this.tagTitle(uniqueBounces, hardBounce)}
                tagSubtitle={`${this.tagSubtitle(uniqueBounces, hardBounce)}%`}
                tagClass={this.tagTitle(uniqueBounces, hardBounce) === 'Poor' ? '' : '--al-success'}/>
              <ProgressComponent
                title={'SOFT BOUNCE'}
                heading={`${uniqueSoftBounces}%`}
                tagTitle={this.tagTitle(uniqueSoftBounces, softBounce)}
                tagSubtitle={`${this.tagSubtitle(uniqueSoftBounces, softBounce)}%`}
                tagClass={this.tagTitle(uniqueSoftBounces, hardBounce) === 'Poor' ? '' : '--al-success'}/>
              <li className='stw-list-item'>
                <div className='stw-column'>
                  <h2 className='stw-title'>SPAM REPORTS</h2>
                  <h3 className='stw-heading'>1</h3>
                </div>
              </li>
              <li className='stw-list-item'>
                <div className='stw-column'>
                  <h2 className='stw-title'>EMAILS SENT</h2>
                  <h3 className='stw-heading'>{totalDelivered}K</h3>
                </div>
              </li>
            </ul>
            <div className="analytics">
              <div className="engagement-chart">
                <div>
                  <KendoChart.Chart transitions={false} pannable={false} zoomable={false}>
                    <KendoChart.ChartLegend position="bottom" orientation="horizontal"/>
                    <KendoChart.ChartTooltip
                      render={({points}: KendoChart.SharedTooltipContext) => (
                        <div className="engagement-tooltip">
                          <div>{this.state.record.timestamp || ''}</div>
                          {points.map((point) => (
                            <div key={point.series.name + point.value} style={{color: point.series.color}}>
                              {point.series.name}: {point.value}%
                            </div>
                          ))}
                        </div>
                      )}
                      shared={true}/>
                    <KendoChart.ChartCategoryAxis>
                      {this.state.record.length < Constants.INTERVAL_THRESHOLD &&
                          <KendoChart.ChartCategoryAxisItem
                              categories={this.state.record.map((item) => item.timestamp)}/>}
                    </KendoChart.ChartCategoryAxis>
                    <KendoChart.ChartSeries>
                      <KendoChart.ChartSeriesItem
                        type="line"
                        color={totalOpenColor}
                        field="totalOpen"
                        name="Total Open"
                        data={this.state.record}
                      >
                        {this.state.record && this.state.record.length < Constants.INTERVAL_THRESHOLD &&
                            <KendoChart.ChartSeriesLabels format="{0:N2}%"/>}
                      </KendoChart.ChartSeriesItem>
                      <KendoChart.ChartSeriesItem
                        type="line"
                        color={uniqueBouncesColor}
                        field="uniqueBounces"
                        name="Total Unique Bounces"
                        data={this.state.record}
                      >
                        {this.state.record && this.state.record.length < Constants.INTERVAL_THRESHOLD &&
                            <KendoChart.ChartSeriesLabels format="{0:N2}%"/>}
                      </KendoChart.ChartSeriesItem>
                      <KendoChart.ChartSeriesItem
                        type="line"
                        color={uniqueSoftBouncesColor}
                        field="uniqueSoftBounces"
                        name="Total Unique Soft Bounces"
                        data={this.state.record}
                      >
                        {this.state.record && this.state.record.length < Constants.INTERVAL_THRESHOLD &&
                            <KendoChart.ChartSeriesLabels format="{0:N2}%"/>}
                      </KendoChart.ChartSeriesItem>
                      <KendoChart.ChartSeriesItem
                        type="line"
                        color={totalClickColor}
                        field="totalClick"
                        name="Total Click"
                        data={this.state.record}
                      >
                        {this.state.record && this.state.record.length < Constants.INTERVAL_THRESHOLD &&
                            <KendoChart.ChartSeriesLabels format="{0:N2}%"/>}
                      </KendoChart.ChartSeriesItem>
                    </KendoChart.ChartSeries>
                  </KendoChart.Chart>
                </div>
              </div>
            </div>
          </div>
        </div> :
        <div>
          <p className="no-data-tag">{Constants.NO_DATA_COPY}</p>
        </div> }
      </>
    )
  }
}

class ProgressComponent extends EmailHealthComponent {
  render() {
    const {title, heading, tagTitle, tagSubtitle, tagClass} = this.props
    return (
      <li className='stw-list-item'>
        <div className='stw-column'>
          <h2 className='stw-title'>{title}</h2>
          <h3 className='stw-heading'>{heading}</h3>
          <div className={`stw-tag-wrapper ${tagClass}`}>
            <h5 className='stw-tag-title'>{tagTitle}</h5>
            <h6 className='stw-tag-subtitle'>Benchmark: {tagSubtitle}</h6>
          </div>
        </div>
      </li>
    )
  }
}

export default ProgressComponent
export const AnalyticsOpensClicksComponent: ConnectedComponentClass<ComponentType<OpensClicksClass>, Fields> = GenericRedux.registerNewComponent(
  OpensClicksClass, 'analytics_opensclicks', {})
